/**
 * @fileoverview Syndicator Showdown — Public Stable Profile Page (Phase 7)
 * @module pages/PublicStableProfilePage
 *
 * Public route: /stable/:stableSlug — no authentication required.
 *
 * Two plain document reads, zero computation on page load:
 *   1. stable_slugs/{stableSlug} -> { uid }
 *   2. player_profiles/{uid}     -> full public profile (stats/badges/
 *      recentShowdowns/latestResults are all pre-computed server-side by
 *      functions/src/profileStats.js after settlement — see docs there).
 *
 * Never reads/renders email, phone, address, auth provider, or the raw uid.
 *
 * Layout (2026-07-06 QA fix): a logged-in visitor sees this exact content
 * rendered INSIDE the normal AppShell (desktop sidebar / mobile bottom nav)
 * so they never lose app navigation, including when viewing their own
 * profile. A logged-out visitor sees the standalone public/branded layout
 * (same pattern as ShareStablePage). Auth state is resolved BEFORE picking
 * a shell so there's no flash of the wrong layout.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { AppShell } from '../components/shell/AppShell';
import { DEFAULT_PROFILE_IMAGE } from '../utils/profileImage';
import './PublicStableProfilePage.css';

// QA fix 2026-07-06 (Phase 7 stat accuracy round 2): financialAudit.
// totalPrizeDividendsWon / leaderboard.totalEarnings / earningsBreakdown
// dividends are stored and displayed as plain dollars everywhere else in
// the app (Dashboard, My Stable, Leaderboard, Share cards all use the
// exact same '$' + Math.round(n).toLocaleString() with NO /100) — there is
// no cents-scale convention here. Dividing by 100 here was the actual bug
// that made this page under-report winnings 100x versus every other page.
function fmt$(amount) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return '$' + Math.round(amount).toLocaleString('en-AU');
}

function ordinal(n) {
  if (n === null || n === undefined) return '—';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function formatMemberSince(ts) {
  const date = ts?.toDate ? ts.toDate() : null;
  if (!date) return null;
  return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
}

function formatMeetingDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function rankClass(rank) {
  if (rank === 1) return 'sp-showdown-row__rank--1';
  if (rank === 2) return 'sp-showdown-row__rank--2';
  if (rank === 3) return 'sp-showdown-row__rank--3';
  return '';
}

// Phase 8 — display-only mirror of the `comingSoon: true` entries in
// functions/src/achievements/achievementDefinitions.js (requires rating
// engine integration — Phase 12). These are never evaluated/earned; they
// only render as greyed-out locked tiles so players know what's next.
// Keep in sync with that file if the deferred achievement set ever changes.
const COMING_SOON_ACHIEVEMENTS = [
  { key: 'value_hunter',      title: 'Value Hunter' },
  { key: 'roughie_reward',    title: 'Roughie Reward' },
  { key: 'crowd_contrarian',  title: 'Crowd Contrarian' },
];

/**
 * Public-safe avatar resolution for THIS page only (never reads another
 * user's private users/{uid} doc — player_profiles/{uid} is the only
 * public-safe source). Preference order matches the rest of the app
 * (see utils/profileImage.js#getProfileImageUrl): an explicitly set
 * avatarUrl/photoURL, then the shared default silhouette. A bare-initial
 * fallback is only ever shown if even the default image asset fails to
 * load (see onError below) — it is not the normal no-avatar state.
 */
function resolveAvatarSrc(profile) {
  return profile?.avatarUrl || profile?.photoURL || DEFAULT_PROFILE_IMAGE;
}

/** Best-effort SEO — updates the tab title + meta description for this page only. */
function useProfileSeo(profile) {
  useEffect(() => {
    if (!profile) return undefined;
    const prevTitle = document.title;
    const name = profile.username || profile.displayName || 'A Stable';
    document.title = `${name}'s Stable Profile | Syndicator Showdown`;

    let metaTag = document.querySelector('meta[name="description"]');
    const prevContent = metaTag?.getAttribute('content') || null;
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('name', 'description');
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute(
      'content',
      `View ${name}'s Syndicator Showdown stable profile, virtual winnings, trophy cabinet and recent results.`
    );

    return () => {
      document.title = prevTitle;
      if (metaTag && prevContent !== null) metaTag.setAttribute('content', prevContent);
    };
  }, [profile]);
}

/** Logged-out header — branded, with Login + a Join Now CTA (QA fix item 9). */
function PublicTopbar() {
  return (
    <header className="sp-topbar">
      <Link to="/" className="sp-topbar__brand">
        <img src="/logo.png" alt="" className="sp-topbar__logo" />
        <span className="sp-topbar__brand-text">Syndicator Showdown</span>
      </Link>
      <div className="sp-topbar__actions">
        <Link to="/signin" className="sp-topbar__login">Login</Link>
        <Link to="/signin?mode=signup" className="sp-topbar__join">Join Now</Link>
      </div>
    </header>
  );
}

/**
 * Picks the layout: AppShell (sidebar/bottom-nav) for a resolved,
 * logged-in visitor; the standalone branded public layout otherwise.
 * `isLoggedIn` must only ever be `true`/`false` here, never `undefined` —
 * callers gate on auth resolution first so there's no shell flash.
 */
function PageShell({ isLoggedIn, children }) {
  if (isLoggedIn) {
    return (
      <AppShell>
        <div className="sp-page sp-page--in-shell">
          <div className="sp-container">{children}</div>
        </div>
      </AppShell>
    );
  }
  return (
    <div className="sp-page">
      <div className="sp-container">
        <PublicTopbar />
        {children}
      </div>
    </div>
  );
}

function NotFoundBody() {
  return (
    <div className="sp-empty">
      <p className="sp-empty__icon">🐎</p>
      <h2 className="sp-empty__title">Stable Not Found</h2>
      <p className="sp-empty__text">This stable profile doesn't exist or may have changed.</p>
      <Link to="/showdowns" className="sp-cta__btn sp-cta__btn--primary">Browse Showdowns</Link>
    </div>
  );
}

function PrivateProfileBody() {
  return (
    <div className="sp-empty">
      <p className="sp-empty__icon">🔒</p>
      <h2 className="sp-empty__title">This Profile is Private</h2>
      <p className="sp-empty__text">This player has chosen to keep their stable profile private.</p>
      <Link to="/leaderboards" className="sp-cta__btn sp-cta__btn--primary">View Leaderboards</Link>
    </div>
  );
}

export function PublicStableProfilePage() {
  const { stableSlug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [currentUser, setCurrentUser] = useState(undefined); // undefined = auth not yet resolved
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  useEffect(() => onAuthStateChanged(auth, setCurrentUser), []);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const slugSnap = await getDoc(doc(db, 'stable_slugs', stableSlug));
      if (!slugSnap.exists()) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const uid = slugSnap.data().uid;

      const profileSnap = await getDoc(doc(db, 'player_profiles', uid));
      if (!profileSnap.exists()) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setProfile({ uid, ...profileSnap.data() });
    } catch (e) {
      console.error('[PublicStableProfile] Load error:', e.code, e.message, e);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [stableSlug]);

  useEffect(() => { loadProfile(); }, [loadProfile]);
  useEffect(() => { setAvatarFailed(false); }, [profile?.uid]);

  useProfileSeo(profile);

  // Auth state not yet resolved, or the profile fetch is still in flight —
  // hold in the public shell until we KNOW whether to switch to AppShell,
  // so a logged-in visitor never sees a flash of the public layout first.
  const authResolved = currentUser !== undefined;
  const isLoggedIn = authResolved && !!currentUser;

  if (!authResolved || loading) {
    return (
      <PageShell isLoggedIn={isLoggedIn}>
        <div className="sp-spinner-wrap"><div className="ss-spinner" /></div>
      </PageShell>
    );
  }

  if (notFound || !profile) {
    return <PageShell isLoggedIn={isLoggedIn}><NotFoundBody /></PageShell>;
  }

  const isOwner = currentUser && currentUser.uid === profile.uid;

  // Profile Visibility toggle (AccountPage.jsx) — the owner can always see
  // their own profile regardless of the toggle; every other visitor
  // (including logged-out) is turned away without any profile data leaking.
  if (profile.profilePublic === false && !isOwner) {
    return <PageShell isLoggedIn={isLoggedIn}><PrivateProfileBody /></PageShell>;
  }

  const stats = profile.stats || {};
  // Phase 8 — badges is now a list of REAL, server-verified earned
  // achievements only (functions/src/achievements/achievementEngine.js
  // updateProfileBadgeSummary) — no more Phase 7 locked/derived entries.
  const badges = profile.badges || [];
  const achievementStats = profile.achievementStats || null;
  const recentShowdowns = profile.recentShowdowns || [];
  const latestResults = profile.latestResults || [];
  const memberSince = formatMemberSince(profile.createdAt);
  const publicName = profile.username || profile.displayName || 'Stable Owner';
  const avatarSrc = resolveAvatarSrc(profile);

  return (
    <PageShell isLoggedIn={isLoggedIn}>
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div className="sp-hero">
        <div className="sp-hero__avatar">
          {avatarFailed ? (
            <div className="sp-hero__initials">{publicName[0]?.toUpperCase() || 'S'}</div>
          ) : (
            <img
              src={avatarSrc}
              alt={publicName}
              className="sp-hero__img"
              onError={() => setAvatarFailed(true)}
            />
          )}
        </div>
        <div className="sp-hero__identity">
          <h1 className="sp-hero__name">{publicName}</h1>
          {profile.foundingMember && (
            <span className="sp-hero__badge">⭐ Founding Member</span>
          )}
          {profile.bio && <p className="sp-hero__bio">&ldquo;{profile.bio}&rdquo;</p>}
          {memberSince && <span className="sp-hero__joined">Member since {memberSince}</span>}
        </div>
      </div>

      <div className="sp-hero__stats">
        <div className="sp-hero__stat">
          <span className="sp-hero__stat-value">{fmt$(stats.totalVirtualWinnings)}</span>
          <span className="sp-hero__stat-label">Virtual Earnings</span>
        </div>
        <div className="sp-hero__stat">
          <span className="sp-hero__stat-value">{stats.gamesPlayed ?? 0}</span>
          <span className="sp-hero__stat-label">Games Played</span>
        </div>
        <div className="sp-hero__stat">
          <span className="sp-hero__stat-value">
            {stats.bestFinish ? ordinal(stats.bestFinish) : '—'}
          </span>
          <span className="sp-hero__stat-label">Best Finish</span>
        </div>
        <div className="sp-hero__stat">
          <span className="sp-hero__stat-value">
            {stats.totalRunnersOwned ? `${Math.round((stats.top5StrikeRate || 0) * 100)}%` : '—'}
          </span>
          <span className="sp-hero__stat-label">Top 5 Rate</span>
        </div>
      </div>

      <div className="sp-hero__actions">
        <Link to="/showdowns" className="sp-cta__btn sp-cta__btn--primary">Enter a Showdown →</Link>
        <Link to="/leaderboards" className="sp-cta__btn sp-cta__btn--secondary">View Leaderboards</Link>
        {/* Scrolls to the Trophy Cabinet card further down THIS page — never
            navigates away — so it works identically for the profile owner
            and any other visitor viewing this stable. */}
        <button
          type="button"
          className="sp-cta__btn sp-cta__btn--secondary"
          onClick={() => document.getElementById('trophy-cabinet')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          View Trophy Cabinet
        </button>
        {isOwner && (
          <Link to="/account" className="sp-cta__btn sp-cta__btn--secondary">Edit Profile</Link>
        )}
      </div>

      {/* ── Trophy cabinet + stats ───────────────────────────────────── */}
      <div className="sp-grid-2">
        <div id="trophy-cabinet" className="sp-card sp-trophies">
          <h2 className="sp-section-title">🏆 Trophy Cabinet</h2>

          {achievementStats && achievementStats.totalAchievements > 0 && (
            <div className="sp-trophy-stats">
              <span className="sp-trophy-stat">
                {achievementStats.totalAchievements} achievement{achievementStats.totalAchievements === 1 ? '' : 's'}
              </span>
              {achievementStats.platinumBadges > 0 && (
                <span className="sp-trophy-stat sp-trophy-stat--platinum">
                  💎 {achievementStats.platinumBadges}
                </span>
              )}
              {achievementStats.goldBadges > 0 && (
                <span className="sp-trophy-stat sp-trophy-stat--gold">
                  🥇 {achievementStats.goldBadges}
                </span>
              )}
              {achievementStats.silverBadges > 0 && (
                <span className="sp-trophy-stat sp-trophy-stat--silver">
                  🥈 {achievementStats.silverBadges}
                </span>
              )}
              {achievementStats.bronzeBadges > 0 && (
                <span className="sp-trophy-stat sp-trophy-stat--bronze">
                  🥉 {achievementStats.bronzeBadges}
                </span>
              )}
            </div>
          )}

          <div className="sp-trophies__grid">
            {/* player_profiles.badges can currently be written by two
                producers with different shapes: the Phase 8 achievement
                engine (achievementKey/title/tier — genuinely earned only)
                and the legacy pre-Phase-8 profileStats.js deriveBadges()
                (key/label/earned, no tier — can include earned:false
                entries). Normalise field names here and drop anything not
                actually earned so a stale legacy write never shows a blank
                title/tier or a not-yet-earned badge as if it were earned. */}
            {badges
              .filter(badge => badge.earned !== false)
              .map(badge => {
                const badgeKey = badge.achievementKey || badge.key;
                const title = badge.title || badge.achievementTitle || badge.label || 'Achievement';
                const tier = (badge.tier || 'bronze').toLowerCase();
                return (
                  <div
                    key={badgeKey}
                    className={`sp-trophy sp-trophy--${tier}`}
                    title={title}
                  >
                    <span className="sp-trophy__icon">{badge.icon}</span>
                    <span className="sp-trophy__label">{title}</span>
                    <span className="sp-trophy__tier">{tier.toUpperCase()}</span>
                  </div>
                );
              })}

            {COMING_SOON_ACHIEVEMENTS.map(d => (
              <div key={d.key} className="sp-trophy sp-trophy--locked" title="Coming soon">
                <span className="sp-trophy__icon">🔒</span>
                <span className="sp-trophy__label">{d.title}</span>
                <span className="sp-trophy__tier">Coming Soon</span>
              </div>
            ))}
          </div>

          {badges.length === 0 && (
            <p className="sp-empty-note">Trophies will appear as this stable earns achievements.</p>
          )}
        </div>

        <div className="sp-card sp-stats">
          <h2 className="sp-section-title">📊 Stable Stats</h2>
          <div className="sp-stat-list">
            <div className="sp-stat-row">
              <span className="sp-stat-row__label">Games Played</span>
              <span className="sp-stat-row__value">{stats.gamesPlayed ?? '—'}</span>
            </div>
            <div className="sp-stat-row">
              <span className="sp-stat-row__label">Virtual Earnings</span>
              <span className="sp-stat-row__value">{fmt$(stats.totalVirtualWinnings)}</span>
            </div>
            <div className="sp-stat-row">
              <span className="sp-stat-row__label">Best Finish</span>
              <span className="sp-stat-row__value">
                {stats.bestFinish ? ordinal(stats.bestFinish) : '—'}
              </span>
            </div>
            <div className="sp-stat-row">
              <span className="sp-stat-row__label">Winners Owned</span>
              <span className="sp-stat-row__value">{stats.totalWinnersOwned ?? '—'}</span>
            </div>
            <div className="sp-stat-row">
              <span className="sp-stat-row__label">Top 5 Runners</span>
              <span className="sp-stat-row__value">{stats.totalTop5Owned ?? '—'}</span>
            </div>
            <div className="sp-stat-row">
              <span className="sp-stat-row__label">Win Rate</span>
              <span className="sp-stat-row__value">
                {stats.totalRunnersOwned ? `${Math.round((stats.winStrikeRate || 0) * 100)}%` : '—'}
              </span>
            </div>
            <div className="sp-stat-row">
              <span className="sp-stat-row__label">Top 5 Rate</span>
              <span className="sp-stat-row__value">
                {stats.totalRunnersOwned ? `${Math.round((stats.top5StrikeRate || 0) * 100)}%` : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Recent showdowns ─────────────────────────────────────────── */}
      <div className="sp-card">
        <h2 className="sp-section-title">📋 Recent Showdowns</h2>
        {recentShowdowns.length === 0 ? (
          <p className="sp-empty-note">🏇 This stable hasn't entered a Showdown yet.</p>
        ) : (
          recentShowdowns.map(sd => (
            <Link key={sd.slateId} to={`/leaderboard/${sd.slateId}`} className="sp-showdown-row sp-showdown-row--link">
              <span className="sp-showdown-row__name">{sd.gameName}</span>
              <span className="sp-showdown-row__date">{formatMeetingDate(sd.meetingDate)}</span>
              <span className={`sp-showdown-row__rank ${rankClass(sd.rank)}`}>
                {sd.rank ? `#${sd.rank}` : '—'}
              </span>
              <span className="sp-showdown-row__earnings">{fmt$(sd.virtualWinnings)}</span>
            </Link>
          ))
        )}
      </div>

      {/* ── Latest results ───────────────────────────────────────────── */}
      <div className="sp-card">
        <h2 className="sp-section-title">🏇 Latest Results</h2>
        {latestResults.length === 0 ? (
          <p className="sp-empty-note">Results will appear once races settle.</p>
        ) : (
          latestResults.map((r, idx) => (
            <div key={`${r.slateId}_${r.raceId}_${r.runnerName}_${idx}`} className="sp-showdown-row">
              <span className="sp-showdown-row__name">
                {r.runnerName}
                {(r.raceName || r.showdownName) && (
                  <span className="sp-showdown-row__context">
                    {[r.raceName, r.showdownName].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
              <span className="sp-showdown-row__date">{formatMeetingDate(r.meetingDate)}</span>
              <span className={`sp-showdown-row__rank ${rankClass(r.finishPos)}`}>
                {r.finishPos ? ordinal(r.finishPos) : '—'}
              </span>
              <span className="sp-showdown-row__earnings">{fmt$(r.virtualPrize)}</span>
            </div>
          ))
        )}
      </div>

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <div className="sp-cta">
        <p className="sp-cta__text">Think you can build a better stable?</p>
        <div className="sp-cta__buttons">
          <Link to="/showdowns" className="sp-cta__btn sp-cta__btn--primary">Enter a Showdown →</Link>
          <Link to="/leaderboards" className="sp-cta__btn sp-cta__btn--secondary">View Leaderboards</Link>
        </div>
      </div>
    </PageShell>
  );
}

export default PublicStableProfilePage;
